import React, { useMemo, useContext, useEffect, useState, useRef } from 'react';
import { CompanyReactContext } from '../../../../contexts/CompanyContext';
import { getCompanyFileDownloadLinkAPI } from '../../../../Services/api';
import { questionnaireInputFieldType } from '../../../Questionnaires/QuestionnaireConstants';
import { get } from 'lodash';

/**
 * SlideThumbnail: non-editable, compact visual for a slide/chapter.
 * Keep minimal for reuse; Step 3 can enhance with live content preview.
 *
 * @param {object} params - Component parameters
 * @param {object} params.item - Original item (e.g., chapter)
 * @param {boolean} [params.isSuperAdminMode=false] - If true, skip CompanyFile API calls (PublicAssets mode)
 */
export const SlideThumbnail = ({ item, isSuperAdminMode = false }) => {
    const { companyLogoUrl } = useContext(CompanyReactContext);
    const title = item?.title || '';
    const backgroundImageUrl = item?.backgroundImage?.signedUrl || null;
    const [backgroundVideoUrl, setBackgroundVideoUrl] = useState(item?.backgroundVideo?.signedUrl || null);
    const components = Array.isArray(item?.components) ? item.components : [];

    // Use ref to measure actual rendered width for scale calculation
    const canvasRef = useRef(null);
    const [canvasWidth, setCanvasWidth] = useState(200); // default fallback

    // Base canvas in editor is 800x450 (16:9)
    const baseWidth = 800;
    const baseHeight = 450;
    const scaleX = canvasWidth / baseWidth;
    const scaleY = scaleX; // maintain aspect ratio (16:9)

    // Measure canvas width on mount and resize
    useEffect(() => {
        const measureWidth = () => {
            if (canvasRef.current) {
                const width = canvasRef.current.offsetWidth;
                if (width > 0) {
                    setCanvasWidth(width);
                }
            }
        };

        measureWidth();

        // Use ResizeObserver for dynamic resizing
        const resizeObserver = new ResizeObserver(measureWidth);
        if (canvasRef.current) {
            resizeObserver.observe(canvasRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const [resolvedAssetUrlById, setResolvedAssetUrlById] = useState({});

    const layers = useMemo(() => {
        const subset = components; // render all; adjust if performance needed
        return subset.map((comp) => {
            const left = (comp.x || 0) * scaleX;
            const top = (comp.y || 0) * scaleY;
            const width = (comp.width || 200) * scaleX;
            const height = (comp.height === 'auto' ? 40 : (comp.height || 100)) * scaleY;

            if (comp.type === 'text') {
                const content = typeof comp.content === 'string' ? comp.content : '';
                const fontSize = (comp.fontSize || 16) * scaleY;
                const color = comp.color || '#333';
                const fontWeight = comp.bold ? '700' : '400';
                const fontStyle = comp.italic ? 'italic' : 'normal';
                const textDecoration = comp.underline ? 'underline' : 'none';
                const background = comp.backgroundColor || 'transparent';
                const opacity = comp.opacity !== undefined ? comp.opacity : 1;
                const direction = comp.direction || 'rtl';
                return (
                    <div
                        key={comp.id}
                        className="thumb-layer text"
                        style={{ left, top, width, height, color, fontSize, fontWeight, fontStyle, textDecoration, background, opacity, overflow: 'hidden', direction, zIndex: 2 }}
                    >
                        {content}
                    </div>
                );
            }

            if (comp.type === 'shape') {
                const bg = comp.backgroundColor || '#000000';
                const borderColor = comp.borderColor || 'transparent';
                const borderWidth = comp.borderWidth || 0;
                const opacity = comp.opacity !== undefined ? comp.opacity : 1;
                const tl = (comp.borderRadius?.topLeft || 0);
                const tr = (comp.borderRadius?.topRight || 0);
                const br = (comp.borderRadius?.bottomRight || 0);
                const bl = (comp.borderRadius?.bottomLeft || 0);
                const radius = (tl || tr || br || bl) ? `${tl * scaleX}px ${tr * scaleX}px ${br * scaleX}px ${bl * scaleX}px / ${tl * scaleY}px ${tr * scaleY}px ${br * scaleY}px ${bl * scaleY}px` : undefined;
                return (
                    <div
                        key={comp.id}
                        className="thumb-layer shape"
                        style={{ left, top, width, height, background: bg, borderColor, borderWidth, borderStyle: 'solid', opacity, borderRadius: radius, zIndex: 2 }}
                    />
                );
            }

            if (comp.type === 'image' || comp.type === 'logo') {
                const opacity = comp.opacity !== undefined ? comp.opacity : 1;
                const contentUrl = typeof comp.content === 'string' && comp.content.startsWith('http') ? comp.content : null;
                const resolvedUrl = (!contentUrl && typeof comp.content === 'string') ? resolvedAssetUrlById[comp.content] : null;
                const src = comp.type === 'logo' ? (companyLogoUrl || null) : (comp.signedUrl || contentUrl || resolvedUrl || null);
                const bgColor = comp.backgroundColor || 'transparent';
                const bgOpacity = comp.opacity !== undefined ? comp.opacity : 1;
                const tl = (comp.borderRadius?.topLeft || 0);
                const tr = (comp.borderRadius?.topRight || 0);
                const br = (comp.borderRadius?.bottomRight || 0);
                const bl = (comp.borderRadius?.bottomLeft || 0);
                const radius = (tl || tr || br || bl) ? `${tl * scaleX}px ${tr * scaleX}px ${br * scaleX}px ${bl * scaleX}px / ${tl * scaleY}px ${tr * scaleY}px ${br * scaleY}px ${bl * scaleY}px` : undefined;
                if (src) {
                    return (
                        <React.Fragment key={comp.id}>
                            {(bgColor !== 'transparent' || bgOpacity < 1) && (
                                <div
                                    key={`${comp.id}-bg`}
                                    className="thumb-layer image-bg"
                                    style={{ left, top, width, height, position: 'absolute', backgroundColor: bgColor, opacity: bgOpacity, borderRadius: radius, zIndex: 2 }}
                                />
                            )}
                            <img
                                key={`${comp.id}-img`}
                                className="thumb-layer image"
                                src={src}
                                alt=""
                                style={{ left, top, width, height, position: 'absolute', objectFit: 'contain', opacity: 1, borderRadius: radius, zIndex: 3 }}
                            />
                        </React.Fragment>
                    );
                }
                // Fallback placeholder box
                return (
                    <div
                        key={comp.id}
                        className="thumb-layer image no-src"
                        style={{ left, top, width, height, opacity, zIndex: 2 }}
                    />
                );
            }

            if (comp.type === 'video') {
                const opacity = comp.opacity !== undefined ? comp.opacity : 1;
                const contentUrl = typeof comp.content === 'string' && comp.content.startsWith('http') ? comp.content : null;
                const resolvedUrl = (!contentUrl && typeof comp.content === 'string') ? resolvedAssetUrlById[comp.content] : null;
                const src = comp.signedUrl || contentUrl || resolvedUrl || null;
                if (src) {
                    return (
                        <video
                            key={comp.id}
                            className="thumb-layer video"
                            src={src}
                            muted
                            playsInline
                            preload="metadata"
                            style={{ left, top, width, height, position: 'absolute', objectFit: 'cover', opacity, zIndex: 2 }}
                        />
                    );
                }
                // Minimal video placeholder
                return (
                    <div
                        key={comp.id}
                        className="thumb-layer video no-src"
                        style={{ left, top, width, height, opacity, zIndex: 2 }}
                    />
                );
            }

            return null;
        });
    }, [components, companyLogoUrl, scaleX, scaleY, resolvedAssetUrlById]);

    // Resolve background video signed URL if only an ID is present
    // Skip API call in SuperAdmin mode (PublicAssets already have signedUrls)
    useEffect(() => {
        let isMounted = true;
        const bgVid = item?.backgroundVideo;
        if (!bgVid) return;
        if (bgVid.signedUrl) {
            setBackgroundVideoUrl(bgVid.signedUrl);
            return;
        }
        // In SuperAdmin mode, PublicAssets should already have signedUrl
        // Don't call CompanyFile API with PublicAsset IDs
        if (isSuperAdminMode) {
            return;
        }
        if (bgVid.id) {
            (async () => {
                try {
                    const url = await getCompanyFileDownloadLinkAPI({ fileId: bgVid.id });
                    if (isMounted) setBackgroundVideoUrl(url);
                } catch (e) {
                    // Ignore; fallback to image/neutral background
                }
            })();
        }
        return () => {
            isMounted = false;
        };
    }, [item?.backgroundVideo, isSuperAdminMode]);

    // Resolve image/video component URLs when content holds a file ID
    // Skip API call in SuperAdmin mode (PublicAssets already have signedUrls)
    useEffect(() => {
        // In SuperAdmin mode, PublicAssets should already have signedUrl in components
        // Don't call CompanyFile API with PublicAsset IDs
        if (isSuperAdminMode) {
            return;
        }

        let isMounted = true;
        const unresolvedIds = components
            .filter(c => (c.type === 'image' || c.type === 'video') && !c.signedUrl && typeof c.content === 'string' && !c.content.startsWith('http'))
            .map(c => c.content);
        const uniqueIds = Array.from(new Set(unresolvedIds)).filter(id => !resolvedAssetUrlById[id]);
        if (uniqueIds.length === 0) return;

        (async () => {
            try {
                const entries = await Promise.all(uniqueIds.map(async (id) => {
                    try {
                        const url = await getCompanyFileDownloadLinkAPI({ fileId: id });
                        return [id, url];
                    } catch {
                        return [id, null];
                    }
                }));
                if (!isMounted) return;
                const additions = {};
                entries.forEach(([id, url]) => { if (url) additions[id] = url; });
                if (Object.keys(additions).length > 0) {
                    setResolvedAssetUrlById(prev => ({ ...prev, ...additions }));
                }
            } catch {
                // ignore batch errors
            }
        })();

        return () => { isMounted = false; };
    }, [components, resolvedAssetUrlById, isSuperAdminMode]);

    // Check if this is a question section
    const hasQuestion = item?.question;
    const question = item?.question || {};
    const questionText = get(question, 'text', '');
    const questionType = get(question, 'type', '');
    const options = get(question, 'options', []);

    return (
        <div className="slide-thumb" title={title}>
            <div className="thumb-canvas" ref={canvasRef}>
                {backgroundVideoUrl ? (
                    <video className="thumb-background-video" src={backgroundVideoUrl} muted playsInline preload="metadata" />
                ) : backgroundImageUrl ? (
                    <div className="thumb-background" style={{ backgroundImage: `url(${backgroundImageUrl})` }} />
                ) : (
                    <div className="thumb-background" />
                )}

                {hasQuestion ? (
                    <div className="thumb-question-overlay" style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                        borderRadius: `${Math.max(2, 3 * scaleY)}px`,
                        border: `${Math.max(1, 0.3 * scaleY)}px solid #efb713`,
                        boxShadow: `0 ${Math.max(1, 0.2 * scaleY)}px ${Math.max(2, 0.8 * scaleY)}px rgba(0, 0, 0, 0.1)`,
                        padding: `${Math.max(4, 2 * scaleY)}px ${Math.max(4, 1.5 * scaleX)}px`,
                        overflow: 'hidden',
                        direction: 'rtl'
                    }}>
                        <div style={{
                            position: 'relative',
                            width: '95%',
                            display: 'flex',
                            flexDirection: 'column',
                            paddingLeft: `${Math.max(2, 0.4 * scaleX)}px`,
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                fontSize: `${Math.max(10, 14 * scaleY)}px`,
                                fontWeight: 500,
                                marginBottom: `${Math.max(4, 1.8 * scaleY)}px`,
                                paddingBottom: `${Math.max(3, 1 * scaleY)}px`,
                                paddingRight: `${Math.max(6, 2 * scaleX)}px`,
                                borderBottom: `${Math.max(1, 0.2 * scaleY)}px solid #efb713`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'right'
                            }}>
                                {questionText}
                            </div>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: `${Math.max(2, 0.8 * scaleY)}px`,
                                overflow: 'hidden'
                            }}>
                                {options.slice(0, 5).map((option) => {
                                    const optionText = get(option, 'text', '');
                                    const isCorrect = get(option, 'isCorrect', false);
                                    return (
                                        <div key={option.id} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: `${Math.max(2, 1 * scaleX)}px`,
                                            fontSize: `${Math.max(8, 10 * scaleY)}px`,
                                            fontWeight: 500,
                                            paddingTop: `${Math.max(2, 1 * scaleY)}px`,
                                            paddingBottom: `${Math.max(2, 1 * scaleY)}px`,
                                            paddingLeft: `${Math.max(4, 1.4 * scaleX)}px`,
                                            paddingRight: `${Math.max(4, 1.4 * scaleX)}px`,
                                            borderRadius: '50px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                marginRight: `${Math.max(3, 1 * scaleX)}px`,
                                                marginTop: `${Math.max(1, 0.3 * scaleY)}px`,
                                                position: 'relative'
                                            }}>
                                                <div style={{
                                                    width: `${Math.max(8, 2.2 * scaleY)}px`,
                                                    height: `${Math.max(8, 2.2 * scaleY)}px`,
                                                    border: `${Math.max(1, 0.2 * scaleY)}px solid ${isCorrect ? '#efb713' : '#BDBCBC'}`,
                                                    borderRadius: questionType === 'MULTIPLE_CHOICE' ? '50%' : `${Math.max(1, 0.3 * scaleY)}px`,
                                                    flexShrink: 0,
                                                    backgroundColor: '#ffffff',
                                                    position: 'relative'
                                                }}>
                                                    {isCorrect && questionType === 'MULTIPLE_CHOICE' && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            width: '60%',
                                                            height: '60%',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#efb713'
                                                        }} />
                                                    )}
                                                    {isCorrect && questionType === 'CHECKBOX' && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            left: '50%',
                                                            top: '20%',
                                                            width: `${Math.max(3, 0.8 * scaleY)}px`,
                                                            height: `${Math.max(5, 1.2 * scaleY)}px`,
                                                            border: 'solid #efb713',
                                                            borderWidth: `0 ${Math.max(1, 0.2 * scaleY)}px ${Math.max(1, 0.2 * scaleY)}px 0`,
                                                            transform: 'translate(-50%, 0) rotate(45deg)'
                                                        }} />
                                                    )}
                                                </div>
                                            </div>
                                            <div style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                                textAlign: 'right'
                                            }}>
                                                {optionText}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : layers}
            </div>
        </div>
    );
};


